import { zodResolver } from "@hookform/resolvers/zod";
import {
  TODO_NOTE_MAX,
  TODO_TITLE_MAX,
  todoColumnSchema,
  type TodoCardView,
  type TodoColumnValue,
} from "@portal/schemas";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  Textarea,
} from "@portal/ui";
import { Save } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { DialogTitleWithHint } from "../../components/Hint";
import { InlineError } from "../../components/InlineError";
import { TODO_COLUMNS } from "./board";

/** 画面用: 期限は input[type=date] の文字列(空なら期限なし)。 */
const formSchema = z.object({
  title: z.string().trim().min(1, "タスク名を入力してください").max(TODO_TITLE_MAX),
  dueDate: z.string(),
  column: todoColumnSchema,
  note: z.string().max(TODO_NOTE_MAX, `メモは ${TODO_NOTE_MAX} 文字までです`),
});
export type TodoCardFormValues = z.infer<typeof formSchema>;

/**
 * ToDo カードの追加・編集。追加は列ごとの「＋」から開き、状態はその列で始まる(ユーザー要望 2026-10-13)。
 * 取り込んだカード(Backlog 等)のタスク名と期限は取り込み元に合わせて上書きされるので、
 * 状態とメモだけ編集できる。
 */
export function TodoCardDialog({
  open,
  onOpenChange,
  card,
  column,
  onSubmit,
  pending,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 編集するカード。未指定なら追加。 */
  card: TodoCardView | null;
  /** 追加のときの最初の状態(押した「＋」の列)。編集のときはカードの状態。 */
  column: TodoColumnValue;
  onSubmit: (values: {
    title: string;
    dueDate: string | null;
    note: string;
    column: TodoColumnValue;
  }) => void;
  pending: boolean;
  error: unknown;
}) {
  const imported = card?.source != null;
  const form = useForm<TodoCardFormValues>({
    resolver: zodResolver(formSchema),
    values: {
      title: card?.title ?? "",
      dueDate: card?.dueDate ?? "",
      note: card?.note ?? "",
      column: card?.column ?? column,
    },
  });
  const errors = form.formState.errors;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={form.handleSubmit((v) =>
            onSubmit({
              title: v.title,
              dueDate: v.dueDate || null,
              note: v.note,
              column: v.column,
            }),
          )}
          noValidate
          className="flex flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitleWithHint
              hint={
                imported
                  ? "タスク名と期限は取り込み元に合わせて更新されます。ここでは状態とメモだけ変えられます。"
                  : "自分だけのボードです。ほかの人には見えません。"
              }
            >
              {card ? "ToDo を編集" : "ToDo を追加"}
            </DialogTitleWithHint>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="todo-title">タスク名</Label>
            <Input id="todo-title" disabled={imported} {...form.register("title")} />
            {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="todo-due">期限</Label>
              <Input
                id="todo-due"
                type="date"
                className="w-44"
                disabled={imported}
                {...form.register("dueDate")}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="todo-column">状態</Label>
              <NativeSelect id="todo-column" className="w-36" {...form.register("column")}>
                {TODO_COLUMNS.map((c) => (
                  <NativeSelectOption key={c.id} value={c.id}>
                    {c.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="todo-note">メモ</Label>
            <Textarea id="todo-note" rows={4} {...form.register("note")} />
            {errors.note && <p className="text-xs text-destructive">{errors.note.message}</p>}
          </div>
          <InlineError error={error} />
          <DialogFooter>
            <Button type="submit" disabled={pending}>
              <Save />
              保存
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
